import type { PropsWithChildren } from 'react';
import React from 'react';
import BillingConstants from '../../constants/BillingConstants';

import { Box, Button, Heading, Icon, Modal } from '@zillow/constellation-10';
import { IconPlusFilled } from '@zillow/constellation-icons';

import MetroidIframeWrapper from '../MetroidIframeWrapper';
import { EVENT_IDS } from '../../model/ClickstreamEvents';
import type { MetroidPaymentMethodResultType } from '@zg-rentals/rental-manager-env';

const { PAYMENT_TYPES } = BillingConstants;

export const BillingAddPaymentModalContainer = ({ children }: PropsWithChildren) => (
  <Box css={{ position: 'relative', minHeight: '200px', paddingBottom: '16px' }}>{children}</Box>
);

export type BillingAddPaymentModalProps = {
  onCreditCardAdded: (newPaymentMethod: MetroidPaymentMethodResultType) => void;
  accountNumber: string;
  onClose: () => void;
};

const BillingAddPaymentModal = ({ onCreditCardAdded, accountNumber, onClose }: BillingAddPaymentModalProps) => {
  const [selectedType, setSelectedType] = React.useState<string | null>(null);

  const renderTypeSelection = () => (
    <Box css={{ display: 'flex', flexDirection: 'column', gap: 'sm' }}>
      <Button
        css={{ width: '100%', display: 'flex', alignItems: 'center', gap: 'sm' }}
        onClick={() => setSelectedType(PAYMENT_TYPES.CREDIT_CARD)}
        data-eventid={EVENT_IDS.ADD_CREDIT_CARD}
      >
        <Icon size="sm">
          <IconPlusFilled />
        </Icon>{' '}
        Credit card payment
      </Button>
      <Button
        css={{ width: '100%', display: 'flex', alignItems: 'center', gap: 'sm' }}
        onClick={() => setSelectedType(PAYMENT_TYPES.ACH)}
        data-eventid={EVENT_IDS.ADD_ACH}
      >
        <Icon size="sm">
          <IconPlusFilled />
        </Icon>{' '}
        ACH Payment
      </Button>
    </Box>
  );

  const renderIframe = () => {
    if (!selectedType) return null;
    return (
      <MetroidIframeWrapper
        onCreditCardAdded={onCreditCardAdded}
        accountNumber={accountNumber}
        selectedType={selectedType}
      />
    );
  };

  return (
    <Modal
      header={
        <Heading level={6} css={{ marginBottom: 'md' }}>
          Add a new payment method
        </Heading>
      }
      body={
        <BillingAddPaymentModalContainer>
          {selectedType ? renderIframe() : renderTypeSelection()}
        </BillingAddPaymentModalContainer>
      }
      onOpenChange={(val) => {
        if (!val) onClose();
      }}
      open={true}
      size="lg"
    />
  );
};

export default BillingAddPaymentModal;
